import React, { useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';
import { toast } from '@/components/ui/sonner';
import { useOptionalFieldValidation } from '@/components/validation';
import { cn } from '@/lib/utils';
import { RateSheetConfigForm } from '@/components/config_forms/roles/RateSheetConfigForm';
import { getRoleUsageDetails } from '@/services/userConfigService';
import { DeletionDialog } from '@/components/ui/DeletionDialog';


export interface RolesConfigHandle {
  isStepValid: () => boolean;
}

export interface RolesConfigProps {
  onValidityChange?: (valid: boolean) => void;
  checkForClientUsage?: boolean;
  dayRateSettingsCollapsible?: boolean;
}

const RolesConfigContent: React.FC<{
  forwardedRef?: React.ForwardedRef<RolesConfigHandle>;
  onValidityChange?: (valid: boolean) => void;
  checkForClientUsage?: boolean;
  dayRateSettingsCollapsible?: boolean;
}> = ({ forwardedRef, onValidityChange, checkForClientUsage = false, dayRateSettingsCollapsible = true }) => {
  const {
    roles: rolesFromContext,
    billingStructureConfig,
  } = useUserConfigState();
  const { updateRoles: updateRolesInContext, updateBillingStructureConfig } = useUserConfigDispatch();

  const {
    minimumBillableHours,
    halfDayHours,
  } = billingStructureConfig || {};

  const minimumBillableHoursRef = useRef<number | null>(minimumBillableHours);
  useEffect(() => {
    minimumBillableHoursRef.current = minimumBillableHours;
  }, [minimumBillableHours]);

  const roles = rolesFromContext || [];

  // Track editing state from RoleList for validity gating
  const isEditingRef = useRef(false);

  // State for the new delete confirmation dialog
  const [deleteDialogState, setDeleteDialogState] = React.useState<{
    open: boolean;
    type: 'confirm_archive' | 'confirm_delete' | 'confirm_simple_delete';
    roleIndex: number | null;
    usageDetails: {
      has_booking_history: boolean;
      client_names: string[];
    } | null;
  }>({ open: false, type: 'confirm_archive', roleIndex: null, usageDetails: null });

  const anyDayRateRole = (roles || []).some(
    (r) => r.billingStructure === 'day_rate'
  );

  const anyHalfDayRoleEnabled = (roles || []).some(
    (r) => r.billingStructure === 'day_rate' && r.allowHalfDay
  );


  // Field validations via ValidationProvider schemas
  const minBillable = useOptionalFieldValidation('minimumBillableHours');
  const halfDay = useOptionalFieldValidation('halfDayHours');

  const minimumBillableHoursInput = {
    inputProps: {
      'data-validation-key': 'minimumBillableHours',
      value: minimumBillableHours ?? '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value ? parseFloat(e.target.value) : null;
        updateBillingStructureConfig({ minimumBillableHours: v });
      },
      onBlur: minBillable.onBlur,
      className: cn(minBillable.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
    } as Record<string, unknown>,
    errorElement: minBillable.errorElement,
  };

  const halfDayHoursInput = {
    inputProps: {
      'data-validation-key': 'halfDayHours',
      value: halfDayHours ?? '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value ? parseFloat(e.target.value) : null;
        updateBillingStructureConfig({ halfDayHours: v });
      },
      onBlur: halfDay.onBlur,
      className: cn(halfDay.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
    } as Record<string, unknown>,
    errorElement: halfDay.errorElement,
  };

  const handleEditingChange = useCallback((editing: boolean) => {
    isEditingRef.current = editing;
    onValidityChange?.(roles.length > 0 && !editing);
  }, [roles.length, onValidityChange]);

  // Also notify validity when roles count changes
  useEffect(() => {
    onValidityChange?.(roles.length > 0 && !isEditingRef.current);
  }, [roles.length, onValidityChange]);

  useImperativeHandle(forwardedRef, () => ({
    isStepValid: () => {
      if (isEditingRef.current) {
        toast.error("Please save or cancel the open rate form.");
        return false;
      }
      if (!roles || roles.length === 0) {
        toast.error("You must have at least one rate.");
        return false;
      }
      return true;
    },
  }));

  const performDeletion = (index: number) => {
      const roleToRemove = roles[index];
      const roleName = roleToRemove.name;
      const roleSnapshot = { ...roleToRemove };

      const updatedRoles = roles.filter((_, i) => i !== index);
      updateRolesInContext(updatedRoles);
      toast.success(`Removed rate for "${roleName}"`, {
        action: {
          label: 'Undo',
          onClick: () => {
            const current = [...updatedRoles];
            const insertAt = Math.min(index, current.length);
            current.splice(insertAt, 0, roleSnapshot);
            updateRolesInContext(current);
          },
        },
        duration: 5000,
      });
  };

  const handleRemoveRole = async (index: number) => {
    const currentRoles = roles || [];
    const roleToRemove = currentRoles[index];
    if (!roleToRemove) return;

    const isPersistedRole = roleToRemove.id && !String(roleToRemove.id).startsWith('__new__');

    if (checkForClientUsage && isPersistedRole) {
      try {
        const usageDetails = await getRoleUsageDetails(roleToRemove.id!);
        const hasBookingHistory = usageDetails.has_booking_history;
        const isSoftBlocked = usageDetails.client_names.length > 0;

        if (hasBookingHistory) {
          setDeleteDialogState({ open: true, type: 'confirm_archive', roleIndex: index, usageDetails });
          return;
        }

        if (isSoftBlocked) {
          setDeleteDialogState({ open: true, type: 'confirm_delete', roleIndex: index, usageDetails });
          return;
        }
      } catch (error: unknown) {
        toast.error('Could not check rate usage', { description: error instanceof Error ? error.message : String(error) });
        return;
      }
    }

    setDeleteDialogState({ open: true, type: 'confirm_simple_delete', roleIndex: index, usageDetails: null });
  };
  
  // New function to handle the final deletion after confirmation/resolution
  const confirmRemoveRole = () => {
    if (deleteDialogState.roleIndex !== null) {
      performDeletion(deleteDialogState.roleIndex);
    }
    setDeleteDialogState({ open: false, type: 'confirm_archive', roleIndex: null, usageDetails: null });
  };

  return (
    <>
      <RateSheetConfigForm
        title="Rate Card"
        description="Add roles you work as and set each one's billing structure and rate."
        roles={roles || []}
        onRemoveRole={handleRemoveRole}
        onRolesChange={updateRolesInContext}
        onEditingChange={handleEditingChange}
        isUserProfile={true}
        dayRateSettingsCollapsible={dayRateSettingsCollapsible}
        dayRateConfig={{
          title: "Day Rate Settings",
          description: "Configure billing parameters for day rate roles.",
          isMinimumBillableHoursDisabled: !anyDayRateRole,
          isHalfDayConfigDisabled: !anyHalfDayRoleEnabled,
          context: 'user',
          minimumBillableHours: minimumBillableHours,
          halfDayHours: halfDayHours,
          validMinimumBillableHoursForHalfDay: minimumBillableHours ?? 0,
          onMinimumBillableHoursChange: (v: number) => updateBillingStructureConfig({ minimumBillableHours: v }),
          onHalfDayHoursChange: (v: number) => updateBillingStructureConfig({ halfDayHours: v }),
          minimumBillableHoursProps: minimumBillableHoursInput.inputProps,
          halfDayHoursProps: halfDayHoursInput.inputProps,
          minimumBillableHoursError: minimumBillableHoursInput.errorElement,
          halfDayHoursError: halfDayHoursInput.errorElement,
        }}
      />

      <DeletionDialog
        open={deleteDialogState.open}
        onOpenChange={(isOpen) => !isOpen && setDeleteDialogState({ open: false, type: 'confirm_archive', roleIndex: null, usageDetails: null })}
        type={deleteDialogState.type}
        itemType="role"
        itemIndex={deleteDialogState.roleIndex}
        items={roles || []}
        usageDetails={deleteDialogState.usageDetails}
        onConfirmDelete={confirmRemoveRole}
      />
    </>
  );
};

export const RolesConfig = forwardRef<RolesConfigHandle, RolesConfigProps>(
  ({ onValidityChange, checkForClientUsage, dayRateSettingsCollapsible }, ref) => (
    <RolesConfigContent forwardedRef={ref} onValidityChange={onValidityChange} checkForClientUsage={checkForClientUsage} dayRateSettingsCollapsible={dayRateSettingsCollapsible} />
));

RolesConfig.displayName = 'RolesConfig';
